import axios from 'axios';
import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';
import app from '../app';
import { BackboneViewProps, ModelTypes, ModelTypesPoints } from '../types';
import {
  capitalizeEachWord,
  getColorForSegment,
  getModelLabels,
} from './utils';
import ModelDataModel from '../models/model/ModelDataModel';
import { IS_HYBRID, IS_LLTB, MODEL_TYPES_NAMES } from '../utils/constants';
import { getStudioBaseUrl } from '../utils/studioUrl';
import { PredictionsTopbar } from './component/PredictionsTopbar';
import { PredictionsBanner } from './component/PredictionsBanner';
import { getBongoUrl } from '../common/utils/bongo';

const template = require('./predictions.pug');

function getScoresIncluded(modelType: ModelTypes) {
  return getModelLabels(modelType).map((label) => ({
    label: capitalizeEachWord(label),
    color: getColorForSegment(label),
  }));
}

type ModelBoxProps = {
  id: number;
  name: string;
  type: string;
  isPrimary: boolean;
  editable: boolean;
  tenant: number;
};

export class ModelBox extends React.Component<ModelBoxProps, any> {
  render() {
    const { type, name, isPrimary } = this.props;

    if (document.getElementById('MQA')) {
      document.getElementById('MQA').removeAttribute('hidden');
    }
    if (document.getElementById('likelihood')) {
      document.getElementById('likelihood').removeAttribute('hidden');
    }
    if (document.getElementById('alert')) {
      document.getElementById('alert').removeAttribute('hidden');
    }

    const getModelData = () => {
      switch (type) {
        case 'pql':
        case 'pql2':
          return [
            {
              displayName: MODEL_TYPES_NAMES[type],
              displayType: 'behavioral',
              description:
                "By using your in-app data, we'll tell you exactly when your leads are engaged with your product so that you can talk to them when it's the most relevant time.",
              scoresIncluded: getScoresIncluded('pql'),
              datasources: [
                'Website Activity',
                'Product Usage',
                'Email Activity',
                'Marketing Campaigns',
              ],
            },
            {
              displayName: `Lead Grade${
                IS_HYBRID(type) ? ` - ${MODEL_TYPES_NAMES[type]}` : ''
              }`,
              displayType: 'lead_grade',
              description:
                'Combining the Customer Fit score and Likelihood to Buy score, the Lead Grade surfaces your best and most engaged leads.',
              scoresIncluded: getScoresIncluded('lead_grade'),
              datasources: [
                'Website Activity',
                'Product Usage',
                'Email Activity',
                'Marketing Campaigns',
              ],
            },
          ];
        case 'mql':
          return [
            {
              displayName: MODEL_TYPES_NAMES[type],
              displayType: 'behavioral',
              description:
                "By using your marketing data, we'll tell you exactly when your leads are showing interest in your brand so that you can talk to them when it's the most relevant time.",
              scoresIncluded: getScoresIncluded('pql'),
              datasources: [
                'Website Activity',
                'Email Activity',
                'Marketing Campaigns',
              ],
            },
            {
              displayName: `Lead Grade${
                IS_HYBRID(type) ? ` - ${MODEL_TYPES_NAMES[type]}` : ''
              }`,
              displayType: 'lead_grade',
              description:
                'Combining the Customer Fit score and Likelihood to Buy score, the Lead Grade surfaces your best and most engaged leads.',
              scoresIncluded: getScoresIncluded('lead_grade'),
              datasources: [
                'Website Activity',
                'Email Activity',
                'Marketing Campaigns',
              ],
            },
          ];
        case 'mqa':
          return [
            {
              displayName: MODEL_TYPES_NAMES[type],
              displayType: 'behavioral',
              description:
                'By using your in-app data, we’ll tell you exactly when your accounts are engaged with your product so that you can talk to them when it’s the most relevant time.',
              scoresIncluded: getScoresIncluded('mqa'),
              datasources: [
                'Website Activity',
                'Product Usage',
                'Email Activity',
                'Marketing Campaigns',
              ],
            },
          ];
        default:
          return [
            {
              displayName: `Customer Fit${!isPrimary ? ` - ${name}` : ''}`,
              displayType: 'customer_fit',
              description:
                'The customer fit model automatically researches your leads and identifies those who are a good fit to buy from you.',
              scoresIncluded: getScoresIncluded('customer_fit'),
              datasources: [
                'Firmographic data: Company Size, Industry, Geo Location, etc.',
                'Demographic data: Job Title, Seniority, etc.',
                'Technographic data: Tech Stack',
              ],
            },
          ];
      }
    };

    const modelCards = getModelData().map((modelData) => {
      const {
        displayName,
        displayType,
        description,
        scoresIncluded,
        datasources,
      } = modelData;
      return (
        <div key={`model_box_${displayName}`} id={String(this.props.id)}>
          <div className="box-shadow-3d card mt-md-2 d-flex">
            <div className="h5 card-header">
              {' '}
              {displayName}
              <div className="float-right badge badge-success">LIVE</div>
            </div>
            <div className="card-body">
              <p>{description}</p>
              <span>
                <b>Scores Included:</b>
              </span>
              {scoresIncluded.map((score) => {
                return (
                  <span
                    key={`${displayName}_${score.label}`}
                    className={`badge badge-${score.color} p-2 ml-1 mb-2 text-white`}
                  >
                    {score.label}
                  </span>
                );
              })}
              <p>
                <b>Data Sources Included:</b>
              </p>
              {datasources.map((datasource) => (
                <p key={`${displayName}_${datasource}`}>{datasource}</p>
              ))}
              <div className="row">
                <div className="col-auto">
                  <a
                    className="btn btn-primary my-2.my-lg-0 mr-1 text-white"
                    href={`${getStudioBaseUrl()}/tenant/${
                      this.props.tenant
                    }/models/${this.props.id}/overview`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Model &rarr;
                  </a>
                </div>
                <div
                  className={`col-auto integration-item--plays ${displayType}`}
                  id={String(this.props.id)}
                >
                  <a className="button btn btn-primary my-2.my-lg-0 mr-1 text-white">
                    View Performance &rarr;
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
    return <div>{modelCards}</div>;
  }
}

type ListModelContainerProps = {
  id: string;
  list: ModelDataModel[];
  editable: boolean;
  tenant: number;
};

export class ListModelContainer extends React.Component<
  ListModelContainerProps,
  {}
> {
  sortedLiveListOfModels: ModelDataModel[];

  add() {
    Backbone.history.navigate(
      `/org/${this.props.tenant}/predictions/performances/models/new/admin`,
      true
    );
  }

  render() {
    // Sorted based on model type
    const sortedLiveListOfModels = this.props.list
      .sort((a: ModelDataModel, b: ModelDataModel) => {
        return ModelTypesPoints[b.type] - ModelTypesPoints[a.type];
      })
      .filter(({ live }) => live);

    if (!sortedLiveListOfModels.length)
      return (
        <div className="card box-shadow-3d d-flex h-100 border-primary bg-primary-fade text-primary alert-No-Data mt-4">
          <div className="card-body align-middle">
            <div className="spinner-border float-left mr-2"></div>
            <h5>
              <b>
                Looks like no model has been deployed yet. Head to the Data
                Studio to create or deploy one.
              </b>
            </h5>
          </div>
        </div>
      );

    return (
      <>
        {sortedLiveListOfModels.map(({ modelId, name, type, is_primary }) => (
          <ModelBox
            key={modelId}
            id={modelId}
            name={name}
            type={type}
            isPrimary={is_primary}
            tenant={this.props.tenant}
            editable={this.props.editable}
          />
        ))}
      </>
    );
  }
}

export default Backbone.View.extend({
  events: {
    'click .integration-item--plays.behavioral': 'open_diagnostics',
    'click .integration-item--plays.lead_grade': 'open_diagnostics_lead_grade',
    'click .integration-item--plays.customer_fit': 'open_performances',
  },

  async initialize(options: BackboneViewProps) {
    this.tenant = options.tenant;
    this.session = app.session;
    const user = this.session.get('user');
    this.modifiable = user.superKudu;
    // Initialize models as empty array in case of error
    this.models = [];
    this.hasError = false;
    try {
      await this.get_models();
    } catch (error) {
      console.error('Failed to load models:', error);
      this.hasError = true;
      // Models already initialized as empty array, continue rendering
    }
    this.render();
    this.render_topbar();
    this.render_list_models();
  },
  render_topbar() {
    // Create a hidden container for the topbar components
    // They will inject themselves into the layout via global functions
    const topbarContainer = document.createElement('div');
    topbarContainer.id = 'predictions-topbar-container';
    topbarContainer.style.display = 'none';
    document.body.appendChild(topbarContainer);

    ReactDOM.render(
      <>
        <PredictionsTopbar />
      </>,
      topbarContainer
    );
  },
  open_performances(e: any) {
    const idn = e.currentTarget.id;
    Backbone.history.navigate(
      `/org/${this.tenant}/predictions/performances/models/${idn}`,
      true
    );
  },
  open_diagnostics(e: any) {
    const idn = e.currentTarget.id;
    Backbone.history.navigate(
      `/org/${this.tenant}/predictions/performances/models/${idn}/diagnostics`,
      true
    );
  },
  open_diagnostics_lead_grade(e: any) {
    const idn = e.currentTarget.id;
    Backbone.history.navigate(
      `/org/${this.tenant}/predictions/performances/models/${idn}/diagnostics/lead_grade`,
      true
    );
  },
  is_likelihood_active() {
    if (!Array.isArray(this.models)) {
      this.isLikelihood = false;
      return;
    }
    this.models.forEach((model: any) => {
      if (model?.live && IS_LLTB(model.type)) {
        this.isLikelihood = true;
      }
    });
  },
  is_MQA_active() {
    if (!Array.isArray(this.models)) {
      this.isMQA = false;
      return;
    }
    this.models.forEach((model: any) => {
      if (model?.live && model.type === 'mqa') {
        this.isMQA = true;
      }
    });
  },
  render() {
    const html = template({
      isMQA: this.isMQA,
      isLikelihood: this.isLikelihood,
      tenant: this.tenant,
      studioBaseUrl: getStudioBaseUrl(),
    });
    this.$el.html(html);

    // Render banner as React component within this view's element
    // This ensures it's scoped to this view and cleaned up automatically
    let bannerContainer = this.$el.find('#predictions-banner-container')[0];
    if (!bannerContainer) {
      bannerContainer = document.createElement('div');
      bannerContainer.id = 'predictions-banner-container';
      bannerContainer.style.cssText = 'width: 100%;';
      // Insert at the beginning of this view's element
      this.$el.prepend(bannerContainer);
    }

    // Render banner component
    ReactDOM.render(<PredictionsBanner />, bannerContainer);

    // Add action buttons to the page content area (above the models list, separate from banner)
    const buttonsContainer = $(
      '<div class="tw-flex tw-gap-2 tw-mb-4 tw-justify-end"></div>'
    );
    buttonsContainer.html(`
      <a
        href="https://help.madkudu.com/docs/introduction-to-madkudu-data-studio"
        target="_blank"
        rel="noopener noreferrer"
        class="tw-no-underline"
      >
        <button class="tw-px-4 tw-py-2 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-medium tw-rounded-md tw-text-sm tw-transition-colors">
          Documentation
        </button>
      </a>
      <a
        href="${getStudioBaseUrl()}/tenant/${this.tenant}"
        target="_blank"
        rel="noopener noreferrer"
        class="tw-no-underline"
        title="Build or edit models, view samples of records, create signals for your Sales team and much more!"
      >
        <button class="tw-px-4 tw-py-2 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-medium tw-rounded-md tw-text-sm tw-transition-colors">
          Data Studio
        </button>
      </a>
    `);

    // Insert buttons before the models list container
    const listModelsContainer = $('.list__models', this.$el);
    if (listModelsContainer.length > 0) {
      listModelsContainer.before(buttonsContainer);
    } else {
      // Fallback: append to the container
      $('.container', this.$el).first().prepend(buttonsContainer);
    }

    // If there was an error, display it in the models list area
    if (this.errorHtml && $('.list__models', this.$el).length > 0) {
      $('.list__models', this.$el).html(this.errorHtml);
    }

    // Scope tooltip initialization to this view's element only to avoid conflicts with sidebar tooltips
    this.$el.find('[data-toggle="tooltip"]').tooltip();
    return this;
  },
  remove() {
    // Clean up banner when view is removed
    // Check both within this view's element and globally (in case of leftover from old code)
    const bannerContainerInView = this.$el.find(
      '#predictions-banner-container'
    )[0];
    if (bannerContainerInView) {
      ReactDOM.unmountComponentAtNode(bannerContainerInView);
    }

    // Also clean up any global banner container that might exist outside this view (legacy cleanup)
    const globalBannerContainer = document.getElementById(
      'predictions-banner-container'
    );
    if (
      globalBannerContainer &&
      !this.$el[0]?.contains(globalBannerContainer)
    ) {
      ReactDOM.unmountComponentAtNode(globalBannerContainer);
      globalBannerContainer.remove();
    }

    // Call parent remove method
    return Backbone.View.prototype.remove.call(this);
  },
  async get_models() {
    try {
      const url = `${getBongoUrl()}/v1/org/${this.tenant}/models`;
      const res = await (axios as any).get(url, {
        crossDomain: true,
        params: { tenant: this.tenant },
      });
      this.models = res.data || [];
      this.is_likelihood_active();
      this.is_MQA_active();
    } catch (error) {
      // Log error for debugging
      console.error('Error fetching models:', error);

      // Set models to empty array to prevent further errors
      this.models = [];

      // Show user-friendly error message
      const errorMessage =
        error?.response?.status === 500
          ? 'Unable to load models at this time. Please try refreshing the page or contact support if the problem persists.'
          : 'Failed to load models. Please try again later.';

      // Display error in the page content area
      if (this.$el && this.$el.length > 0) {
        const errorHtml = `
          <div class="card box-shadow-3d d-flex h-100 border-danger bg-danger-fade text-danger alert-No-Data mt-4">
            <div class="card-body align-middle">
              <h5>
                <b>${errorMessage}</b>
              </h5>
              ${
                error?.response?.status
                  ? `<p class="mb-0"><small>Error code: ${error.response.status}</small></p>`
                  : ''
              }
            </div>
          </div>
        `;
        // Store error HTML to render after template renders
        this.errorHtml = errorHtml;
      }

      // Re-throw to be caught by initialize
      throw error;
    }
  },
  async render_list_models() {
    // Don't render if there was an error (error message already displayed in render)
    if (this.errorHtml) {
      return;
    }

    // Ensure models is an array
    if (!Array.isArray(this.models)) {
      this.models = [];
    }

    this.models = this.models.sort((prev: any, next: any) => {
      return String(prev.type).charAt(0) > String(next.type).charAt(0) ? 1 : -1;
    });

    const listContainer = $('.list__models', this.$el)[0];
    if (listContainer) {
      ReactDOM.render(
        <ListModelContainer
          id="listModels"
          list={this.models}
          tenant={this.tenant}
          editable={this.modifiable}
        />,
        listContainer
      );
    }
  },
});
